"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Mail,
  Save as SaveIcon,
} from "lucide-react";
import { WorkspaceSectionHeader } from "../workspace-section-header";
import { toast } from "sonner";
import { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { SDK } from "@/@sdk/sdk";
import { useSDKActionWithDeps } from "@/@sdk/sdk.hooks";
import {
  iOrganizationEmailConfig,
  QuoteEmailDeliveryMethod,
} from "@/@sdk/brands/brand-model";
import { resolveOrganizationEmailConfig } from "@/@sdk/brands/quote-email-config.helpers";
import {
  isCustomQuoteEmailConfigured,
  resolveQuoteEmailFromDisplay,
} from "@/@sdk/brands/platform-email-config.helpers";
import { selectOrganizationDomain } from "@/store/modules/workspace/workspace.selectors";
import { useBrandById } from "@/hooks/use-brands";
import { ResendDomainSetup } from "./resend-domain-setup";
import { PlatformEmailDefaultBanner } from "./platform-email-default-banner";
import { cn } from "@/lib/utils";

const deliveryMethodSchema = z.enum([
  "CUSTOM_SMTP",
  "BOOKING_EMAIL_DOMAIN",
  "CUSTOM_EMAIL_DOMAIN",
]);

const quoteEmailConfigSchema = z
  .object({
    deliveryMethod: deliveryMethodSchema,
    subject: z.string().optional(),
    senderName: z.string().optional(),
    senderEmail: z.string().email().optional().or(z.literal("")),
    server: z.string().optional(),
    port: z.coerce.number().optional(),
    email: z.string().email().optional().or(z.literal("")),
    username: z.string().optional(),
    password: z.string().optional(),
    secure: z.boolean().optional(),
    preCTAEmailTemplate: z.string().optional(),
    postCTAEmailTemplate: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.deliveryMethod === "CUSTOM_SMTP") {
      if (!data.server?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "SMTP server is required",
          path: ["server"],
        });
      }
      if (!data.username?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Username is required",
          path: ["username"],
        });
      }
      if (!data.password?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Password is required",
          path: ["password"],
        });
      }
      if (!data.port || data.port <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Port is required",
          path: ["port"],
        });
      }
    }
  });

type QuoteEmailConfigFormValues = z.infer<typeof quoteEmailConfigSchema>;

const DELIVERY_OPTIONS: {
  value: QuoteEmailDeliveryMethod;
  title: string;
  description: string;
}[] = [
  {
    value: "CUSTOM_SMTP",
    title: "Custom SMTP",
    description: "Send quotes through your own SMTP server.",
  },
  {
    value: "BOOKING_EMAIL_DOMAIN",
    title: "Booking Email Domain",
    description: "Send as quotes@your verified booking domain",
  },
  {
    value: "CUSTOM_EMAIL_DOMAIN",
    title: "Custom Email Domain",
    description: "Add and verify a separate domain for quote emails.",
  },
];

function buildEmailConfigPayload(
  data: QuoteEmailConfigFormValues,
): iOrganizationEmailConfig {
  const base: iOrganizationEmailConfig = {
    deliveryMethod: data.deliveryMethod,
    subject: data.subject || undefined,
    senderName: data.senderName || undefined,
    preCTAEmailTemplate: data.preCTAEmailTemplate || undefined,
    postCTAEmailTemplate: data.postCTAEmailTemplate || undefined,
  };

  if (data.deliveryMethod === "CUSTOM_SMTP") {
    return {
      ...base,
      server: data.server,
      port: data.port,
      email: data.email || undefined,
      username: data.username,
      password: data.password,
      secure: data.secure || false,
      senderEmail: data.senderEmail || undefined,
    };
  }

  if (data.deliveryMethod === "BOOKING_EMAIL_DOMAIN") {
    return base;
  }

  return {
    ...base,
    senderEmail: data.senderEmail || undefined,
  };
}

export function EmailConfigSection({
  brandId,
  onOpenBrandSection,
}: {
  brandId: string;
  onOpenBrandSection?: (section: "booking-email-config") => void;
}) {
  const brand = useBrandById(brandId);
  const organizationDomain = useSelector(selectOrganizationDomain);
  const emailConfig = resolveOrganizationEmailConfig(brand?.emailConfig);
  const bookingDomain = brand?.resendEmailConfig;

  const quoteEmailFromDisplay = useMemo(
    () => resolveQuoteEmailFromDisplay(brand, organizationDomain),
    [brand, organizationDomain]
  );
  const usesPlatformQuoteEmail = !isCustomQuoteEmailConfigured(brand);

  const { doAction: saveBrand, isProcessing: isSaving } =
    useSDKActionWithDeps(
      () => ({
        action:
          (SDK) => (edits: { emailConfig: iOrganizationEmailConfig }) =>
            SDK.brands.editById(brandId, edits),
        failureMessage: "Failed to save quote email configuration.",
        throwError: true,
      }),
      [brandId],
    );

  const form = useForm<QuoteEmailConfigFormValues>({
    resolver: zodResolver(quoteEmailConfigSchema),
    defaultValues: {
      deliveryMethod: emailConfig.deliveryMethod,
      server: emailConfig.server || "",
      port: emailConfig.port || 465,
      email: emailConfig.email || "",
      username: emailConfig.username || "",
      password: emailConfig.password || "",
      secure: emailConfig.secure || false,
      senderName: emailConfig.senderName || "",
      senderEmail: emailConfig.senderEmail || "",
      subject: emailConfig.subject || "",
      preCTAEmailTemplate: emailConfig.preCTAEmailTemplate || "",
      postCTAEmailTemplate: emailConfig.postCTAEmailTemplate || "",
    },
  });

  const deliveryMethod = form.watch("deliveryMethod");

  useEffect(() => {
    if (!brand) return;
    const cfg = resolveOrganizationEmailConfig(brand.emailConfig);
    form.reset({
      deliveryMethod: cfg.deliveryMethod,
      server: cfg.server || "",
      port: cfg.port || 465,
      email: cfg.email || "",
      username: cfg.username || "",
      password: cfg.password || "",
      secure: cfg.secure || false,
      senderName: cfg.senderName || "",
      senderEmail: cfg.senderEmail || "",
      subject: cfg.subject || "",
      preCTAEmailTemplate: cfg.preCTAEmailTemplate || "",
      postCTAEmailTemplate: cfg.postCTAEmailTemplate || "",
    });
  }, [brand, brandId, form]);

  const onSubmit = useCallback(
    async (data: QuoteEmailConfigFormValues) => {
      try {
        const payload = buildEmailConfigPayload(data);

        if (data.deliveryMethod === "CUSTOM_SMTP") {
          await SDK.verifySmtp({
            server: data.server!,
            port: data.port!,
            email: data.email || "",
            username: data.username!,
            password: data.password!,
            secure: data.secure || false,
          } as any);
        }

        if (data.deliveryMethod === "CUSTOM_EMAIL_DOMAIN") {
          const quoteDomain = brand?.emailConfig?.quoteResendEmailConfig;
          if (quoteDomain?.isVerified) {
            if (!data.senderName?.trim()) {
              form.setError("senderName", {
                message: "Sender name is required",
              });
              return;
            }
            if (!data.senderEmail?.trim()) {
              form.setError("senderEmail", {
                message: "Sender email is required",
              });
              return;
            }
          }
        }

        await saveBrand({ emailConfig: payload });
        toast.success("Quote email configuration saved");
      } catch (error) {
        console.error(error);
      }
    },
    [saveBrand, brand?.emailConfig?.quoteResendEmailConfig, form],
  );

  return (
    <div className="space-y-6 px-4 pb-6">
      <WorkspaceSectionHeader
        icon={Mail}
        title="Quote Email Config"
        description="Choose how quote emails are sent for this brand — custom SMTP, your booking Resend domain, a dedicated quote domain, or the TA Desk default."
      />

      {usesPlatformQuoteEmail && quoteEmailFromDisplay?.fromDisplay && (
        <PlatformEmailDefaultBanner
          variant="quote"
          fromAddress={quoteEmailFromDisplay.fromDisplay}
        />
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="deliveryMethod"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Delivery method</FormLabel>
                <FormControl>
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    className="grid gap-3"
                  >
                    {DELIVERY_OPTIONS.map((option) => (
                      <Label
                        key={option.value}
                        htmlFor={`delivery-${option.value}`}
                        className={cn(
                          "flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors",
                          field.value === option.value
                            ? "border-primary bg-primary/5"
                            : "border-border hover:bg-muted/40",
                        )}
                      >
                        <RadioGroupItem
                          value={option.value}
                          id={`delivery-${option.value}`}
                          className="mt-0.5"
                        />
                        <div className="space-y-1">
                          <span className="text-sm font-medium leading-none">
                            {option.title}
                          </span>
                          <p className="text-sm text-muted-foreground">
                            {option.description}
                          </p>
                        </div>
                      </Label>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {deliveryMethod === "CUSTOM_SMTP" && (
            <div className="space-y-4 rounded-lg border p-4">
              <h3 className="text-sm font-medium">SMTP settings</h3>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="server"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>SMTP Server</FormLabel>
                      <FormControl>
                        <Input placeholder="smtp.example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="port"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Port</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email (optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="email@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>SMTP Username</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>SMTP Password</FormLabel>
                      <FormControl>
                        <Input type="password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="secure"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Use secure connection (SSL/TLS)</FormLabel>
                    </div>
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="senderName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sender Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Company Name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="senderEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sender Email</FormLabel>
                      <FormControl>
                        <Input placeholder="quotes@company.com" {...field} />
                      </FormControl>
                      <FormDescription>
                        Used as the From address when set.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          )}

          {deliveryMethod === "BOOKING_EMAIL_DOMAIN" && (
            <div className="space-y-4 rounded-lg border p-4">
              <h3 className="text-sm font-medium">Booking domain</h3>
              {bookingDomain?.isVerified ? (
                <Alert className="border-emerald-200 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-950/20">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  <AlertDescription>
                    Quotes will be sent from{" "}
                    <code className="rounded bg-emerald-100 px-1 dark:bg-emerald-900/40">
                      quotes@{bookingDomain.domain}
                    </code>
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert className="border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/20">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="space-y-2">
                    <p>
                      No verified booking domain yet. Quotes will still send
                      using the TA Desk default until you verify a booking
                      domain, or choose another delivery method.
                    </p>
                    {onOpenBrandSection && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          onOpenBrandSection("booking-email-config")
                        }
                      >
                        <ExternalLink className="mr-2 h-3.5 w-3.5" />
                        Open Booking Email Config
                      </Button>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              <FormField
                control={form.control}
                name="senderName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sender Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={brand?.name || "Company Name"}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Display name shown to recipients. Defaults to brand name.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}

          {deliveryMethod === "CUSTOM_EMAIL_DOMAIN" && (
            <div className="space-y-4 rounded-lg border p-4">
              <h3 className="text-sm font-medium">Quote email domain</h3>
              <ResendDomainSetup brandId={brandId} purpose="quote" />

              <div className="grid grid-cols-2 gap-4 pt-2">
                <FormField
                  control={form.control}
                  name="senderName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sender Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Company Name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="senderEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sender Email</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={
                            brand?.emailConfig?.quoteResendEmailConfig?.domain
                              ? `hello@${brand.emailConfig.quoteResendEmailConfig.domain}`
                              : "hello@quotes.yourcompany.com"
                          }
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Must be an address on your configured quote domain.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          )}

          <FormField
            control={form.control}
            name="subject"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email subject (optional)</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Your Quote - {QUOTE_TITLE}"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Supports tokens like {"{QUOTE_TITLE}"}, {"{CONTACT_FIRST_NAME}"}.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end space-x-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => form.reset()}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <SaveIcon className="mr-2 h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
